import {SeatingPlan} from '@wix/ambassador-seating-v1-seating-plan/types'
import {
  PlaceWithTicketInfo,
  TicketWithFormattedSalePeriod,
  getEventId,
  getPricingOptions,
  hasPricingOptions,
} from '@wix/wix-events-commons-statics'
import {getEvent} from '../../selectors/event'
import {
  getElementIdByPlaceId,
  getPlaceWithTicketInfo,
  getSectionIdByPlaceId,
  isPlaceArea,
  pricingOptionPlaceId,
} from '../../selectors/seating/place'
import {getMappedPlanSections} from '../../selectors/seating/plan'
import {createAsyncAction} from '../../services/redux-toolkit'
import {clearReservationError} from '../reservation-state'

interface DescribeSeatingPlanResponse {
  plan: SeatingPlan
  places: PlaceWithTicketInfo[]
  tickets?: TicketWithFormattedSalePeriod[]
}

export const describeSeatingPlan = createAsyncAction<DescribeSeatingPlanResponse, {refreshTickets: boolean}>(
  'DESCRIBE_SEATING_PLAN',
  async ({refreshTickets}, {getState, extra: {serverApi}}) => {
    const state = getState()
    const event = getEvent(state)
    const eventId = getEventId(event)

    const updatedTickets = refreshTickets ? await serverApi.getAvailableFormattedTickets(eventId) : undefined
    const tickets = updatedTickets ?? state.tickets

    const response = await serverApi.describeSeatingPlan(eventId)

    const categories = response.plan.categories.filter(category => category.totalCapacity)
    const sections = getMappedPlanSections(response.plan)

    const uncategorizedPlaces = response.plan.uncategorizedPlaces
      .filter(place => place.capacity)
      .map(place =>
        getPlaceWithTicketInfo({
          place,
          category: null,
          sections,
          tickets,
          seatingReservationSummary: {places: response.places},
        }),
      )

    const placesWithInfo: PlaceWithTicketInfo[] = [
      ...categories.reduce((places, category) => {
        const {single, whole} = category.places.reduce(
          (acc, place) => {
            if (!place.capacity) {
              return acc
            }

            const elementId = getElementIdByPlaceId(place.id)
            const sectionId = getSectionIdByPlaceId(place.id)

            const section = sections.find(item => item.id === sectionId)
            const element = section.elements.find(item => item.id === elementId)
            const placeWithTicketInfo = getPlaceWithTicketInfo({
              place,
              category,
              sections,
              tickets,
              seatingReservationSummary: {places: response.places},
            })

            const ticket = placeWithTicketInfo.ticket

            if (element.reservationOptions?.reserveWholeElement) {
              const added = acc.whole.find(
                wholeResElement =>
                  `${wholeResElement.sectionId}-${wholeResElement.elementId}` ===
                  `${placeWithTicketInfo.sectionId}-${placeWithTicketInfo.elementId}`,
              )

              if (added) {
                added.places.push(placeWithTicketInfo)
                added.capacity += placeWithTicketInfo.capacity
              } else {
                acc.whole.push({...placeWithTicketInfo, places: [placeWithTicketInfo]})
              }
            } else if (ticket && hasPricingOptions(ticket) && isPlaceArea(place)) {
              const pricingOptions = getPricingOptions(ticket)
              pricingOptions.forEach(({id: pricingOptionId}) => {
                acc.single.push({
                  ...placeWithTicketInfo,
                  id: pricingOptionPlaceId(placeWithTicketInfo.id, pricingOptionId),
                  pricingOptionId,
                })
              })
            } else {
              acc.single.push(placeWithTicketInfo)
            }

            return acc
          },
          {single: [], whole: []} as {single: PlaceWithTicketInfo[]; whole: PlaceWithTicketInfo[]},
        )

        return [...places, ...single, ...whole]
      }, [] as PlaceWithTicketInfo[]),
      ...uncategorizedPlaces,
    ]

    return {
      plan: response.plan,
      places: placesWithInfo,
      tickets: updatedTickets,
    }
  },
)

export const refreshSeatingPlan = createAsyncAction('REFRESH_SEATING_PLAN', async (_, {dispatch}) => {
  await dispatch(describeSeatingPlan({refreshTickets: true}))
  dispatch(clearReservationError())
})
